import {
  DistributionGroup,
  CreateDistributionGroupInput,
  UpdateDistributionGroupInput,
  DeleteDistributionGroupInput
} from '@/types/distributionGroupTypes';

type GetAccessTokenSilentlyType = (options?: any) => Promise<string>;
type GetAccessTokenWithPopupType = (
  options?: any,
  config?: any
) => Promise<string | undefined>;

const API_ROOT = import.meta.env.VITE_API_BASE_URL || '/api';
const API_BASE_URL = `${API_ROOT}/distribution-groups`;

const getAuthToken = async (
  getAccessTokenSilently: GetAccessTokenSilentlyType,
  getAccessTokenWithPopup: GetAccessTokenWithPopupType
): Promise<string> => {
  const authParams = {
    authorizationParams: {
      audience: import.meta.env.VITE_API_AUTH0_AUDIENCE
    }
  };
  try {
    return await getAccessTokenSilently(authParams);
  } catch (error: any) {
    if (
      error?.error === 'consent_required' ||
      error?.error === 'login_required' ||
      error?.error === 'interaction_required'
    ) {
      const token = await getAccessTokenWithPopup(authParams);
      if (!token)
        throw new Error('Failed to acquire token for distribution groups');
      return token;
    }
    throw error;
  }
};

const makeAuthenticatedRequest = async (
  url: string,
  options: RequestInit,
  getAccessTokenSilently: GetAccessTokenSilentlyType,
  getAccessTokenWithPopup: GetAccessTokenWithPopupType
): Promise<Response> => {
  const token = await getAuthToken(
    getAccessTokenSilently,
    getAccessTokenWithPopup
  );
  const response = await fetch(url, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
      ...(options.headers || {})
    }
  });
  if (!response.ok) {
    const errorText = await response.text().catch(() => '');
    throw new Error(
      `API request failed: ${response.status} ${response.statusText} ${errorText}`
    );
  }
  return response;
};

/**
 * Fetches all distribution groups for the current company
 */
export const getDistributionGroups = async (
  companyId: string,
  getAccessTokenSilently: any,
  getAccessTokenWithPopup: any
): Promise<DistributionGroup[]> => {
  const url = `${API_BASE_URL}?companyId=${encodeURIComponent(companyId)}`;
  const resp = await makeAuthenticatedRequest(
    url,
    { method: 'GET' },
    getAccessTokenSilently,
    getAccessTokenWithPopup
  );
  return (await resp.json()) as DistributionGroup[];
};

/**
 * Creates a new distribution group
 */
export const createDistributionGroup = async (
  input: CreateDistributionGroupInput,
  companyId: string,
  getAccessTokenSilently: any,
  getAccessTokenWithPopup: any
): Promise<DistributionGroup> => {
  const payload = { ...input, companyId };
  const resp = await makeAuthenticatedRequest(
    API_BASE_URL,
    { method: 'POST', body: JSON.stringify(payload) },
    getAccessTokenSilently,
    getAccessTokenWithPopup
  );
  return (await resp.json()) as DistributionGroup;
};

/**
 * Updates an existing distribution group
 */
export const updateDistributionGroup = async (
  input: UpdateDistributionGroupInput,
  companyId: string,
  getAccessTokenSilently: any,
  getAccessTokenWithPopup: any
): Promise<DistributionGroup> => {
  const url = `${API_BASE_URL}/${encodeURIComponent(String(input.id))}`;
  const payload = { ...input, companyId };
  const resp = await makeAuthenticatedRequest(
    url,
    { method: 'PUT', body: JSON.stringify(payload) },
    getAccessTokenSilently,
    getAccessTokenWithPopup
  );
  return (await resp.json()) as DistributionGroup;
};

/**
 * Deletes a distribution group
 */
export const deleteDistributionGroup = async (
  input: DeleteDistributionGroupInput,
  companyId: string,
  getAccessTokenSilently: any,
  getAccessTokenWithPopup: any
): Promise<void> => {
  const url = `${API_BASE_URL}/${encodeURIComponent(
    String(input.id)
  )}?companyId=${encodeURIComponent(companyId)}`;
  await makeAuthenticatedRequest(
    url,
    { method: 'DELETE' },
    getAccessTokenSilently,
    getAccessTokenWithPopup
  );
};

/**
 * Tests email delivery for a distribution group
 */
export const testDistributionGroupEmail = async (
  groupId: number,
  getAccessTokenSilently: any,
  getAccessTokenWithPopup: any
): Promise<{ success: boolean; message: string; failedEmails?: string[] }> => {
  const url = `${API_BASE_URL}/${encodeURIComponent(
    String(groupId)
  )}/test-email`;
  const resp = await makeAuthenticatedRequest(
    url,
    { method: 'POST' },
    getAccessTokenSilently,
    getAccessTokenWithPopup
  );
  return (await resp.json()) as {
    success: boolean;
    message: string;
    failedEmails?: string[];
  };
};
